<template>
  <div class="toggle-group" :class="[sizeClass, variantClass, { 'is-disabled': disabled }]" role="radiogroup">
    <button
      type="button"
      v-for="option in options"
      :key="option.value"
      class="toggle-option"
      :class="{ 'is-active': value === option.value }"
      :aria-checked="value === option.value"
      :disabled="disabled || option.disabled"
      role="radio"
      @click="select(option.value)"
    >{{ option.label }}</button>
  </div>
</template>

<script>
export default {
  name: 'segmented-toggle',
  props: {
    value: {
      type: [String, Number, Boolean],
      default: null
    },
    options: {
      type: Array,
      required: true,
      validator: (arr) => arr.every(o => 'value' in o && 'label' in o)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md'].includes(v)
    },
    // 'boxed' = fused button group (default); 'pills' = detached pill buttons
    // matching the app's pill sub-nav language
    variant: {
      type: String,
      default: 'boxed',
      validator: (v) => ['boxed', 'pills'].includes(v)
    }
  },
  computed: {
    sizeClass() {
      return `is-${this.size}`;
    },
    variantClass() {
      return `is-${this.variant}`;
    }
  },
  methods: {
    select(value) {
      if (this.disabled) return;
      this.$emit('input', value);
    }
  }
};
</script>

<style scoped>
.toggle-group {
  display: inline-flex;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: rgba(30, 30, 47, 0.6);
}

.toggle-group.is-md {
  height: 40px;
}

.toggle-group.is-sm {
  height: 32px;
}

.toggle-group.is-disabled {
  opacity: 0.6;
  pointer-events: none;
}

.toggle-option {
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  height: 100%;
  border: none;
  font-family: inherit;
  white-space: nowrap;
}

.toggle-option:focus {
  outline: none;
}

/* A single option can be disabled with `disabled: true` on its entry. */
.toggle-option:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.toggle-option:focus-visible {
  box-shadow: inset 0 0 0 2px rgba(225, 78, 202, 0.6);
}

.is-md .toggle-option {
  padding: 0 0.875rem;
  font-size: 0.8125rem;
}

.is-sm .toggle-option {
  padding: 0 0.75rem;
  font-size: 0.75rem;
}

.toggle-option:not(:last-child) {
  border-right: 1px solid rgba(255, 255, 255, 0.2);
}

.toggle-option:hover:not(.is-active):not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
  color: white;
}

.toggle-option.is-active {
  background: rgba(225, 78, 202, 0.25);
  color: #e14eca;
}

/* Pills variant: detached pill buttons (matches pill sub-nav language) */
.toggle-group.is-pills {
  border: none;
  background: transparent;
  gap: 6px;
  height: auto;
  overflow: visible;
  flex-wrap: wrap;
}
.is-pills .toggle-option {
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  height: auto;
}
.is-pills .toggle-option:not(:last-child) {
  border-right: 1px solid rgba(255, 255, 255, 0.15);
}
.is-pills .toggle-option:hover:not(.is-active):not(:disabled) {
  border-color: rgba(255, 255, 255, 0.35);
  color: rgba(255, 255, 255, 0.9);
  background: transparent;
}
.is-pills .toggle-option.is-active {
  background: rgba(225, 78, 202, 0.15);
  border-color: #e14eca;
  color: #e14eca;
}
</style>

<style>
.white-content .toggle-group {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.15);
}

.white-content .toggle-option {
  color: rgba(0, 0, 0, 0.75);
}

.white-content .toggle-option:not(:last-child) {
  border-right-color: rgba(0, 0, 0, 0.1);
}

.white-content .toggle-option:hover:not(.is-active):not(:disabled) {
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.9);
}

.white-content .toggle-option.is-active {
  background: rgba(225, 78, 202, 0.15);
  color: #b13aa0;
}

.white-content .toggle-group.is-pills {
  background: transparent;
  border: none;
}
.white-content .is-pills .toggle-option {
  border: 1px solid rgba(0, 0, 0, 0.15);
  color: rgba(0, 0, 0, 0.6);
}
.white-content .is-pills .toggle-option:not(:last-child) {
  border-right: 1px solid rgba(0, 0, 0, 0.15);
}
.white-content .is-pills .toggle-option:hover:not(.is-active):not(:disabled) {
  border-color: rgba(0, 0, 0, 0.35);
  color: rgba(0, 0, 0, 0.85);
  background: transparent;
}
.white-content .is-pills .toggle-option.is-active {
  background: rgba(225, 78, 202, 0.15);
  border-color: #b13aa0;
  color: #b13aa0;
}
</style>
